import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  fetchBlogByIdOrSlug,
  fetchBlogs,
  fetchBlogsHome,
  searchBlogs,
} from "../services/blogApi";
import { mapBlog, mapBlogList, mapBlogsHome } from "../utils/blogMappers";

export const useFetchBlogs = ({
  q = "",
  limit = 12,
  category,
  status,
  includeDrafts,
  sort_by,
  sort_dir,
  enabled = true,
} = {}) => {
  return useInfiniteQuery({
    queryKey: [
      "api/blogs",
      { q, limit, category, status, includeDrafts, sort_by, sort_dir },
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const data = await fetchBlogs({
        q,
        page: pageParam,
        limit,
        category,
        status,
        includeDrafts,
        sort_by,
        sort_dir,
      });
      return mapBlogList(data);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.cursor != null && lastPage.cursor !== "") {
        return lastPage.cursor;
      }

      const loadedCount = allPages.reduce(
        (sum, page) => sum + (page?.items?.length || 0),
        0
      );

      if (
        (lastPage?.remainingCount != null && lastPage.remainingCount > 0) ||
        loadedCount < (lastPage?.total || 0)
      ) {
        return allPages.length;
      }

      return undefined;
    },
    enabled,
    placeholderData: (previous) => previous,
  });
};

export const useFetchBlogsHome = (enabled = true) => {
  return useQuery({
    queryKey: ["api/blogs/home"],
    queryFn: async () => {
      const data = await fetchBlogsHome();
      return mapBlogsHome(data);
    },
    enabled,
  });
};

export const useSearchBlogs = (q, enabled = true) => {
  const trimmed = (q || "").trim();
  return useQuery({
    queryKey: ["api/blogs/search", trimmed],
    queryFn: async () => {
      const data = await searchBlogs(trimmed);
      return mapBlogList(data);
    },
    enabled: enabled && Boolean(trimmed),
  });
};

export const useFetchBlog = (idOrSlug, enabled = true) => {
  return useQuery({
    queryKey: ["api/blogs", idOrSlug],
    queryFn: async () => {
      const data = await fetchBlogByIdOrSlug(idOrSlug);
      const raw = data?.data ?? data;
      return mapBlog(raw);
    },
    enabled: enabled && Boolean(idOrSlug),
  });
};
